// These helpers are meant to be used by reducers that handle processing of standard QCS "sync" resources.
import type { RecordOf } from 'immutable';
import { v4 as uuidv4 } from 'uuid';

import { assertInterface } from '../../utils/core';
import { undoActions, undoTypes } from '../undo';

import { mkQcsSyncResourcesPayload } from './resourceStoreTypes';
import type {
  BaseResource,
  QcsSyncResourceStore,
  QcsSyncResourceStoreProps,
  QcsSyncResourcesPayload,
} from './resourceStoreTypes';
import { upsertResource, upsertResources } from './resourceUpserters';
import type { CompareFunc } from './resourceUpserters';

export function makeUndoResource<T extends BaseResource>(
  existingResource: RecordOf<T> | undefined | null,
  newResource: RecordOf<T>,
  afterUpdate?: boolean,
) {
  let undoResource: RecordOf<T> | undefined;
  if (
    newResource.isDeleted &&
    !afterUpdate &&
    assertInterface.assert(existingResource, 'undo delete: existing resource not found')
  ) {
    // action=delete => undoAction=create
    undoResource = existingResource!.delete('id');
    undoResource = undoResource.set('clientId', uuidv4().toUpperCase());
  } else if (
    !existingResource &&
    (afterUpdate === undefined || afterUpdate) &&
    !newResource.isDeleted &&
    newResource.id
  ) {
    // action=create => undoAction=delete
    undoResource = newResource.set('isDeleted', true);
  } else if (!afterUpdate && existingResource) {
    // action=update => undoAction=update
    undoResource = existingResource;
  }

  return undoResource;
}

export function setupUndo(asyncDispatch: (...args: any[]) => any, userMessage: string, executeActions?: any[]) {
  if (asyncDispatch && userMessage) {
    const undo = undoTypes.mkUndo({
      actions: executeActions,
      userMessage,
      key: uuidv4(),
    });
    asyncDispatch(undoActions.addUndo(undo));
  }
}

export function setupUndoForAction(actionToUndo: any, executeActions?: any[]) {
  if (actionToUndo?.asyncDispatch && actionToUndo?.meta?.undo?.userMessage) {
    setupUndo(actionToUndo.asyncDispatch, actionToUndo.meta.undo.userMessage, executeActions);
  }
}

export function upsertResourceSetupUndo<T extends BaseResource, V extends QcsSyncResourceStoreProps<T>>(
  state: QcsSyncResourceStore<T, V>,
  action: any,
  undoActionCreator?: (...args: any[]) => any,
  comparator?: CompareFunc,
) {
  if (action?.meta?.undo?.userMessage) {
    let undoResource: RecordOf<T> | undefined;
    if (!action.meta.undo.disableUndoAction && undoActionCreator && state.resourcesById) {
      const existingResource = state.resourcesById.get(action.payload.id);
      undoResource = action.payload && makeUndoResource(existingResource, action.payload, undefined);
    }
    if (action.meta.undo.disableUndoAction || undoResource) {
      const { undo, ...meta } = action.meta || {};
      setupUndoForAction(action, undoActionCreator && undoResource ? [undoActionCreator(undoResource, meta)] : []);
    }
  }
  return upsertResource(state, action.payload, undefined, undefined, comparator);
}

export function upsertResourcesSetupUndo<
  T extends BaseResource,
  V extends QcsSyncResourceStoreProps<T>,
  S extends { meta?: any; payload: RecordOf<T>[] },
>(
  state: QcsSyncResourceStore<T, V>,
  action: S,
  undoActionCreator?: (...args: any[]) => any,
  afterUpdate = false,
  comparator?: CompareFunc,
) {
  const { payload: resources } = action;
  if (action?.meta?.undo?.userMessage) {
    const undoResources: any[] = [];
    if (!action.meta.undo.disableUndoAction && undoActionCreator && state.resourcesById) {
      resources.forEach((resource) => {
        const existingResource = state.resourcesById.get(resource.id || '');
        const undoResource = makeUndoResource(existingResource, resource, afterUpdate);
        if (undoResource) {
          undoResources.push(undoResource);
        }
      });
    }
    if (!afterUpdate || undoResources?.length) {
      // don't trigger add undo after update, if no undo resources provided
      const { undo, ...meta } = action.meta || {};
      setupUndoForAction(
        action,
        undoActionCreator && undoResources?.length ? [undoActionCreator(undoResources, meta)] : [],
      );
    }
  }

  // We could call createTypedQcsSyncResourcePayloadFactory inline here to pass in the type, but that would be wasteful so I'm just typecasting here.
  const data = mkQcsSyncResourcesPayload({
    resources,
    doMerge: true,
  }) as QcsSyncResourcesPayload<T>;
  return upsertResources(state, data, comparator);
}
